import { useState } from "react";
import { useParams, useLocation, Link } from "wouter";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft,
  Save,
  Loader2,
  Sparkles,
  PencilLine,
  Wand2,
} from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { SalesDiscountForm, emptySalesDiscount, type SalesDiscountFormValue } from "./sales-discount-form";

type Mode = "choose" | "ai" | "form";
// No inline drafting phase: Generate is persist-first — the server creates
// the sale row as a generating draft and the user lands back on the Sales &
// Discounts grid, where an animated build card tracks it (sale-build-card.tsx).

const EXAMPLE_PROMPT =
  "e.g. A Labor Day weekend flash sale — 25% off the starter kit for first-time customers, code STARTER25, running Friday through Monday. Should feel like a farmstand special, not a clearance sale.";

export default function SalesDiscountCreate() {
  const { brandId } = useParams<{ brandId: string }>();
  const [, navigate] = useLocation();
  const { toast } = useToast();

  const [mode, setMode] = useState<Mode>("choose");
  const [value, setValue] = useState<SalesDiscountFormValue>(emptySalesDiscount());

  // ── AI flow state ──────────────────────────────────────────────────────
  const [description, setDescription] = useState("");
  const [submittingBuild, setSubmittingBuild] = useState(false);

  const createMutation = useMutation({
    mutationFn: async (payload: SalesDiscountFormValue) => {
      const res = await apiRequest("POST", `/api/brands/${brandId}/sales-discounts`, payload);
      return res.json();
    },
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/sales-discounts`] });
      toast({ title: "Sale created" });
      navigate(`/brands/${brandId}/sales-discounts/${created.id}`);
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const canSave = !!value.name.trim() && !!value.saleType;

  // Persist-first generation: the server creates the sale row as a generating
  // draft and builds it in the background; we land the user on the Sales &
  // Discounts grid where the animated build card tracks it.
  async function handleGenerate() {
    const desc = description.trim();
    if (!desc || submittingBuild) return;
    setSubmittingBuild(true);
    try {
      await apiRequest("POST", `/api/brands/${brandId}/sale-builder/build`, { description: desc });
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/sales-discounts`] });
      toast({ title: "Building your sale", description: "Watch it come together in the grid." });
      navigate(`/brands/${brandId}/sales-discounts`);
    } catch (e: any) {
      setSubmittingBuild(false);
      toast({ title: "Couldn't start the build", description: e?.message || "Please try again.", variant: "destructive" });
    }
  }

  function startManual() {
    setValue(emptySalesDiscount());
    setMode("form");
  }

  // ── Header ─────────────────────────────────────────────────────────────
  const header = (
    <div className="sticky top-0 z-10 -mx-8 px-8 py-2 bg-background/80 backdrop-blur border-b border-border/40 flex items-center justify-between gap-3">
      <Link href={`/brands/${brandId}/sales-discounts`}>
        <Button variant="ghost" size="sm" className="h-9 -ml-2 text-muted-foreground hover:text-foreground" data-testid="button-back">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Sales
        </Button>
      </Link>
      {mode === "form" && (
        <div className="flex items-center gap-2">
          <Button size="sm" className="h-9 px-4 shadow-sm" onClick={() => createMutation.mutate(value)} disabled={!canSave || createMutation.isPending} data-testid="button-save-sale">
            {createMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save sale
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-4 w-full px-8">
      {header}

      {mode === "choose" && (
        <div className="max-w-3xl mx-auto pt-6">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-semibold">New Sale / Discount</h1>
            <p className="text-sm text-muted-foreground mt-1">How would you like to start?</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Card
              className="relative cursor-pointer hover-elevate border-primary/40 ring-1 ring-primary/20"
              onClick={() => setMode("ai")}
              data-testid="card-mode-ai"
            >
              <CardContent className="p-6 space-y-3">
                <div className="inline-flex items-center justify-center h-11 w-11 rounded-lg bg-primary/10 text-primary">
                  <Sparkles className="h-6 w-6" />
                </div>
                <div>
                  <h2 className="text-base font-semibold flex items-center gap-2">
                    Build with AI
                    <span className="text-[10px] font-medium uppercase tracking-wide rounded-full bg-primary/15 text-primary px-2 py-0.5">Recommended</span>
                  </h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Describe the sale you want to run. We'll draft the whole form for you to review.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card
              className="cursor-pointer hover-elevate"
              onClick={startManual}
              data-testid="card-mode-manual"
            >
              <CardContent className="p-6 space-y-3">
                <div className="inline-flex items-center justify-center h-11 w-11 rounded-lg bg-muted text-foreground">
                  <PencilLine className="h-6 w-6" />
                </div>
                <div>
                  <h2 className="text-base font-semibold">Enter manually</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Fill in the sale form yourself, field by field.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {mode === "ai" && (
        <div className="max-w-2xl mx-auto pt-4">
          <Card>
            <CardContent className="p-6 space-y-4">
                <div className="space-y-1">
                  <h1 className="text-xl font-semibold flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-primary" /> Build with AI
                  </h1>
                  <p className="text-sm text-muted-foreground">
                    Describe your sale in your own words — discount, who it's for, timing, anything that matters.
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium">What sale do you want to run?</Label>
                  <Textarea
                    rows={6}
                    autoFocus
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder={EXAMPLE_PROMPT}
                    data-testid="input-ai-description"
                    onKeyDown={(e) => {
                      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") handleGenerate();
                    }}
                  />
                  <p className="text-xs text-muted-foreground">Tip: press ⌘/Ctrl + Enter to generate.</p>
                </div>
                <div className="flex items-center justify-between gap-2 pt-1">
                  <Button variant="ghost" size="sm" onClick={() => setMode("choose")} data-testid="button-ai-back">
                    <ArrowLeft className="h-4 w-4 mr-2" /> Back
                  </Button>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={startManual}
                      data-testid="button-switch-manual"
                    >
                      Enter manually instead
                    </Button>
                    <Button onClick={handleGenerate} disabled={!description.trim() || submittingBuild} data-testid="button-generate">
                      {submittingBuild ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Wand2 className="h-4 w-4 mr-2" />} Generate sale
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
        </div>
      )}

      {mode === "form" && (
        <Card className="border-border/40">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4 gap-3">
              <h1 className="text-xl font-semibold">New Sale / Discount</h1>
            </div>
            <SalesDiscountForm value={value} onChange={setValue} />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
